import { useState, useEffect } from "react";
import { useInView } from "react-intersection-observer";
import axios from "axios";
import { Star, Quote, MessageCircle, ArrowRight } from "lucide-react";
import LeadCaptureModal from "@/components/LeadCaptureModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollReveal({ children, className, delay = 0 }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={className || ""}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(32px)",
        transition: `opacity 0.7s ease-out ${delay}ms, transform 0.7s ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${i <= rating ? "text-[#D4AF37] fill-[#D4AF37]" : "text-gray-200"}`}
        />
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLeadModal, setShowLeadModal] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials.php`).then((res) => {
      setTestimonials(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="testimonials-page" className="pt-20">
      {/* Hero */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-[#F9FAFB] via-white to-[#EFF6FF]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3B82F6] mb-3 block">
              Client Stories
            </span>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-[#0B1F3A] mb-4">
              What Our Clients Say
            </h1>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              Hear from families and individuals who trust us with their financial future.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="rounded-2xl border border-gray-100 p-8 animate-pulse space-y-4">
                  <div className="h-4 bg-gray-100 rounded w-1/3" />
                  <div className="h-16 bg-gray-100 rounded" />
                  <div className="h-4 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((t, i) => (
                <ScrollReveal key={t.id} delay={i * 100}>
                  <div
                    className="bg-white border border-gray-100 rounded-2xl p-8 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.05)] transition-all duration-300 hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12)] hover:-translate-y-1 h-full flex flex-col"
                    data-testid={`testimonial-card-${i}`}
                  >
                    <Quote className="w-8 h-8 text-[#3B82F6]/20 mb-4" />
                    <p className="text-gray-700 leading-relaxed flex-1 mb-5">
                      "{t.content}"
                    </p>
                    <div>
                      <StarRating rating={t.rating} />
                      <div className="flex items-center gap-3 mt-4">
                        <div className="w-10 h-10 rounded-full bg-[#0B1F3A] flex items-center justify-center text-white font-semibold text-sm">
                          {t.initial}
                        </div>
                        <div>
                          <p className="font-['Outfit'] font-semibold text-[#0B1F3A] text-sm">{t.name}</p>
                          <p className="text-xs text-gray-500">{t.role}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0B1F3A]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <ScrollReveal>
            <h2 className="font-['Outfit'] text-2xl md:text-3xl font-semibold text-white mb-4">
              Ready to Start Your Success Story?
            </h2>
            <p className="text-gray-400 mb-6">
              Join 75+ families who trust Micro Financial Services.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setShowLeadModal(true)}
                className="bg-[#3B82F6] text-white font-semibold px-7 py-3 rounded-full hover:bg-[#2563EB] transition-all shadow-md flex items-center gap-2"
                data-testid="testimonials-cta"
              >
                Get Free Plan <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="whatsapp://send?phone=919324555493"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#25D366] text-white font-semibold px-7 py-3 rounded-full flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" fill="white" /> WhatsApp
              </a>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <LeadCaptureModal open={showLeadModal} onOpenChange={setShowLeadModal} />
    </div>
  );
}
